import { useState, createContext, useContext, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { authApi } from "./api/services";
import { setSentryUser } from "./analytics";
import queryClient from "./api/queryClient";
import UpdatePrompt        from "./components/UpdatePrompt";
import ImpersonationBanner from "./components/ImpersonationBanner";
import MaintenanceBanner   from "./components/MaintenanceBanner";
import SiteBanners         from "./components/SiteBanners";
import { Spinner }         from "./components/UI";
import { T }               from "./tokens";

// ── Eager: hot-path entry routes that we want on first paint. ────
import HomePage          from "./pages/HomePage";
import SalonListing      from "./pages/SalonListing";
import SalonDetail       from "./pages/SalonDetail";
import LoginRegister     from "./pages/LoginRegister";
import Dashboard         from "./pages/Dashboard";

// ── Lazy: rare/heavy routes, split into separate chunks. ─────────
const StylistProfile    = lazy(() => import("./pages/StylistProfile"));
const BookingPage       = lazy(() => import("./pages/BookingPage"));
const BookingDetail     = lazy(() => import("./pages/BookingDetail"));
const MembershipPage    = lazy(() => import("./pages/MembershipPage"));
const LoyaltyPage       = lazy(() => import("./pages/LoyaltyPage"));
const EcommercePage     = lazy(() => import("./pages/EcommercePage"));
const AcademyPage       = lazy(() => import("./pages/AcademyPage"));
const AIRecommendations = lazy(() => import("./pages/AIRecommendations"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const ProfilePage       = lazy(() => import("./pages/ProfilePage"));
const BookingsPage      = lazy(() => import("./pages/BookingsPage"));
const OrdersPage        = lazy(() => import("./pages/OrdersPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage   = lazy(() => import("./pages/VerifyEmailPage"));
const ChatPage          = lazy(() => import("./pages/ChatPage"));

// Partner pages — only relevant for ~1% of users.
const PartnerOnboarding = lazy(() => import("./pages/partner/PartnerOnboarding"));
const PartnerDashboard  = lazy(() => import("./pages/partner/PartnerDashboard"));
const BookingManager    = lazy(() => import("./pages/partner/BookingManager"));
const ServicesSetup     = lazy(() => import("./pages/partner/ServicesSetup"));
const StaffManagement   = lazy(() => import("./pages/partner/StaffManagement"));
const PartnerOffers     = lazy(() => import("./pages/partner/PartnerOffers"));
const PartnerSalonEdit  = lazy(() => import("./pages/partner/PartnerSalonEdit"));
const InventoryPage     = lazy(() => import("./pages/partner/InventoryPage"));

// Admin pages — internal-only.
const AdminDashboard    = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminRefunds         = lazy(() => import("./pages/admin/AdminRefunds"));
const AdminDisputes        = lazy(() => import("./pages/admin/AdminDisputes"));
const AuditLogPage         = lazy(() => import("./pages/admin/AuditLogPage"));
const SystemHealthPage     = lazy(() => import("./pages/admin/SystemHealthPage"));
const ReviewModerationPage = lazy(() => import("./pages/admin/ReviewModerationPage"));
const PhotoModerationPage  = lazy(() => import("./pages/admin/PhotoModerationPage"));
const SystemSettingsPage   = lazy(() => import("./pages/admin/SystemSettingsPage"));
const IntegrationsPage     = lazy(() => import("./pages/admin/IntegrationsPage"));
const PricingPage          = lazy(() => import("./pages/admin/PricingPage"));
const BannerManagerPage    = lazy(() => import("./pages/admin/BannerManagerPage"));
const AnnouncementsPage    = lazy(() => import("./pages/admin/AnnouncementsPage"));
const OnboardingFunnelPage = lazy(() => import("./pages/admin/OnboardingFunnelPage"));
const CohortsPage          = lazy(() => import("./pages/admin/CohortsPage"));
const AiUsagePage          = lazy(() => import("./pages/admin/AiUsagePage"));
const EmailDeliverabilityPage = lazy(() => import("./pages/admin/EmailDeliverabilityPage"));
const BlogManagerPage      = lazy(() => import("./pages/admin/BlogManagerPage"));
const InstructorApplicationsPage = lazy(() => import("./pages/admin/InstructorApplicationsPage"));
const RolesPage            = lazy(() => import("./pages/admin/RolesPage"));
const WebhookManagerPage   = lazy(() => import("./pages/admin/WebhookManagerPage"));
const BulkImportPage       = lazy(() => import("./pages/admin/BulkImportPage"));
const BlogIndex            = lazy(() => import("./pages/BlogIndex"));
const BlogPost             = lazy(() => import("./pages/BlogPost"));

// Marketing — large but rarely the entry route.
const AboutPage          = lazy(() => import("./pages/marketing/AboutPage"));
const HowItWorksPage     = lazy(() => import("./pages/marketing/HowItWorksPage"));
const PartnersPage       = lazy(() => import("./pages/marketing/PartnersPage"));
const StylistsPage       = lazy(() => import("./pages/marketing/StylistsPage"));
const CareersPage        = lazy(() => import("./pages/marketing/CareersPage"));
const ContactPage        = lazy(() => import("./pages/marketing/ContactPage"));
const FAQPage            = lazy(() => import("./pages/marketing/FAQPage"));
const PrivacyPolicyPage  = lazy(() => import("./pages/marketing/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/marketing/TermsOfServicePage"));
const RefundPolicyPage   = lazy(() => import("./pages/marketing/RefundPolicyPage"));

// Centred spinner for the route-level Suspense fallback.
function RouteFallback() {
  return (
    <div role="status" aria-label="Loading page" style={{
      minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: T.dark,
    }}>
      <Spinner size={42} />
    </div>
  );
}

// ── Auth Context ──────────────────────────────────────────────────
export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext) || {};

function AuthProvider({ children }) {
  const [user,    setUser]    = useState(() => { try { const s = localStorage.getItem("fyz_user"); return s ? JSON.parse(s) : null; } catch { return null; } });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("fyz_token");
    if (!token) { setLoading(false); return; }
    authApi.me()
      .then(res  => {
        const u = res.data?.user || res.data?.data;
        setUser(u);
        setSentryUser(u);
        localStorage.setItem("fyz_user", JSON.stringify(u));
      })
      .catch(()  => {
        localStorage.removeItem("fyz_token");
        localStorage.removeItem("fyz_user");
        setUser(null);
        setSentryUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handle = () => { setUser(null); queryClient.clear(); };
    window.addEventListener("fyz:logout", handle);
    return () => window.removeEventListener("fyz:logout", handle);
  }, []);

  const login = (userData, token) => {
    localStorage.setItem("fyz_token", token);
    localStorage.setItem("fyz_user", JSON.stringify(userData));
    setUser(userData);
    setSentryUser(userData);
  };

  const logout = async () => {
    try { await authApi.logout(); } catch { /* ignore */ }
    localStorage.removeItem("fyz_token");
    localStorage.removeItem("fyz_user");
    setUser(null);
    setSentryUser(null);
    queryClient.clear();
  };

  const updateUser = (data) => {
    const updated = { ...user, ...data };
    localStorage.setItem("fyz_user", JSON.stringify(updated));
    setUser(updated);
  };

  if (loading) return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#0D0B08" }}>
      <style>{`@keyframes fyzPulse{from{opacity:.3}to{opacity:1}}`}</style>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:38, color:"#C9A84C", fontStyle:"italic", animation:"fyzPulse 1.4s ease-in-out infinite alternate" }}>Fayriza</div>
    </div>
  );

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, isAuth: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Route guards ──────────────────────────────────────────────────
function PrivateRoute({ children }) {
  const { isAuth } = useAuth();
  const location   = useLocation();
  if (!isAuth) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

function PartnerRoute({ children }) {
  const { isAuth, user } = useAuth();
  const location         = useLocation();
  if (!isAuth) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (!["partner","admin"].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { isAuth, user } = useAuth();
  const location         = useLocation();
  if (!isAuth) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { isAuth } = useAuth();
  if (isAuth) return <Navigate to="/dashboard" replace />;
  return children;
}

function NotFound() {
  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:"#0D0B08", gap:16 }}>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:80, color:"rgba(201,168,76,.2)" }}>404</div>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:"#E8DFC8" }}>Page not found</div>
      <a href="/" style={{ padding:"12px 28px", borderRadius:10, background:"linear-gradient(135deg,#E8C97A,#C9A84C)", color:"#0D0B08", fontSize:14, fontWeight:600, textDecoration:"none" }}>Back to Home</a>
    </div>
  );
}

import Navbar from "./components/Navbar";

// ── App ───────────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MaintenanceBanner />
        <SiteBanners />
        <ImpersonationBanner />
        <UpdatePrompt />
        <Navbar />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public */}
          <Route path="/"                element={<HomePage />} />
          <Route path="/blog"            element={<BlogIndex />} />
          <Route path="/blog/:slug"      element={<BlogPost />} />
          <Route path="/salons"          element={<SalonListing />} />
          <Route path="/salons/:slug"    element={<SalonDetail />} />
          <Route path="/stylists/:id"    element={<StylistProfile />} />
          <Route path="/membership"      element={<MembershipPage />} />
          <Route path="/shop"            element={<EcommercePage />} />
          <Route path="/academy"         element={<AcademyPage />} />
          <Route path="/ai"              element={<AIRecommendations />} />

          {/* Marketing */}
          <Route path="/about"           element={<AboutPage />} />
          <Route path="/how-it-works"    element={<HowItWorksPage />} />
          <Route path="/partners"        element={<PartnersPage />} />
          <Route path="/for-partners"    element={<Navigate to="/partners" replace />} />
          <Route path="/for-stylists"    element={<StylistsPage />} />
          <Route path="/stylists"        element={<StylistsPage />} />
          <Route path="/careers"         element={<CareersPage />} />
          <Route path="/contact"         element={<ContactPage />} />
          <Route path="/faq"             element={<FAQPage />} />
          <Route path="/help"            element={<Navigate to="/faq" replace />} />
          <Route path="/privacy"         element={<PrivacyPolicyPage />} />
          <Route path="/terms"           element={<TermsOfServicePage />} />
          <Route path="/refund-policy"   element={<RefundPolicyPage />} />
          <Route path="/cancellation-policy" element={<Navigate to="/refund-policy" replace />} />

          {/* Guest-only */}
          <Route path="/login"    element={<GuestRoute><LoginRegister /></GuestRoute>} />
          <Route path="/register" element={<GuestRoute><LoginRegister /></GuestRoute>} />

          {/* Public auth flows */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify-email"   element={<VerifyEmailPage />} />

          {/* Customer-protected */}
          <Route path="/book"            element={<PrivateRoute><BookingPage /></PrivateRoute>} />
          <Route path="/dashboard"       element={<PrivateRoute><Dashboard /></PrivateRoute>} />
          <Route path="/bookings"        element={<PrivateRoute><BookingsPage /></PrivateRoute>} />
          <Route path="/bookings/:ref"   element={<PrivateRoute><BookingDetail /></PrivateRoute>} />
          <Route path="/profile"         element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/loyalty"         element={<PrivateRoute><LoyaltyPage /></PrivateRoute>} />
          <Route path="/notifications"   element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
          <Route path="/orders"          element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
          <Route path="/chat"            element={<PrivateRoute><ChatPage /></PrivateRoute>} />

          {/* Partner-protected */}
          <Route path="/partner"           element={<PartnerRoute><PartnerDashboard /></PartnerRoute>} />
          <Route path="/partner/bookings"  element={<PartnerRoute><BookingManager /></PartnerRoute>} />
          <Route path="/partner/services"  element={<PartnerRoute><ServicesSetup /></PartnerRoute>} />
          <Route path="/partner/staff"     element={<PartnerRoute><StaffManagement /></PartnerRoute>} />
          <Route path="/partner/offers"    element={<PartnerRoute><PartnerOffers /></PartnerRoute>} />
          <Route path="/partner/inventory" element={<PartnerRoute><InventoryPage /></PartnerRoute>} />
          <Route path="/partner/salon"     element={<PartnerRoute><PartnerSalonEdit /></PartnerRoute>} />
          <Route path="/partner/onboard"  element={<PartnerOnboarding />} />

          {/* Admin-protected */}
          <Route path="/admin"         element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/salons"  element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/users"   element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/reports" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          <Route path="/admin/refunds" element={<AdminRoute><AdminRefunds /></AdminRoute>} />
          <Route path="/admin/disputes" element={<AdminRoute><AdminDisputes /></AdminRoute>} />
          <Route path="/admin/audit"                element={<AdminRoute><AuditLogPage /></AdminRoute>} />
          <Route path="/admin/health"               element={<AdminRoute><SystemHealthPage /></AdminRoute>} />
          <Route path="/admin/moderation/reviews"   element={<AdminRoute><ReviewModerationPage /></AdminRoute>} />
          <Route path="/admin/moderation/photos"    element={<AdminRoute><PhotoModerationPage /></AdminRoute>} />
          <Route path="/admin/settings"             element={<AdminRoute><SystemSettingsPage /></AdminRoute>} />
          <Route path="/admin/integrations"         element={<AdminRoute><IntegrationsPage /></AdminRoute>} />
          <Route path="/admin/pricing"              element={<AdminRoute><PricingPage /></AdminRoute>} />
          <Route path="/admin/banners"              element={<AdminRoute><BannerManagerPage /></AdminRoute>} />
          <Route path="/admin/broadcast"            element={<AdminRoute><AnnouncementsPage /></AdminRoute>} />
          <Route path="/admin/analytics/funnel"     element={<AdminRoute><OnboardingFunnelPage /></AdminRoute>} />
          <Route path="/admin/analytics/cohorts"    element={<AdminRoute><CohortsPage /></AdminRoute>} />
          <Route path="/admin/analytics/ai-usage"   element={<AdminRoute><AiUsagePage /></AdminRoute>} />
          <Route path="/admin/analytics/email"      element={<AdminRoute><EmailDeliverabilityPage /></AdminRoute>} />
          <Route path="/admin/blog"                 element={<AdminRoute><BlogManagerPage /></AdminRoute>} />
          <Route path="/admin/instructors"          element={<AdminRoute><InstructorApplicationsPage /></AdminRoute>} />
          <Route path="/admin/roles"                element={<AdminRoute><RolesPage /></AdminRoute>} />
          <Route path="/admin/webhooks"             element={<AdminRoute><WebhookManagerPage /></AdminRoute>} />
          <Route path="/admin/bulk-import"          element={<AdminRoute><BulkImportPage /></AdminRoute>} />

          {/* Redirects */}
          <Route path="/home"     element={<Navigate to="/"         replace />} />
          <Route path="/search"   element={<Navigate to="/salons"   replace />} />
          <Route path="/signin"   element={<Navigate to="/login"    replace />} />
          <Route path="/signup"   element={<Navigate to="/register" replace />} />
          <Route path="/store"    element={<Navigate to="/shop"     replace />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
